import AppPaths from '@globals/paths'
import { colors } from '@globals/themes/defaultTheme'
import { Announcement, Award, CalendarEvent, Recognition } from '@kudos/http-client'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import PageLoading from '@shared/loaders/PageLoading'
import { shuffle } from 'lodash'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import 'swiper/css'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import KudosTvItem from './KudosTvItem'
import RecognizeQRCode from './RecognizeQRCode'
import ViewPostQRCode from './ViewPostQRCode'
import useFetchKudosTv from './useFetchKudosTv'

const RECOGNITION_ROTATION_INTERVAL = 40000

const containerStyle: SxProps = {
  position: 'fixed',
  width: '100%',
  height: '100%',
  left: 0,
  top: 0,
  backgroundColor: colors.grayPrimary,
  maxWidth: '100% !important',
  padding: '20px 20px 0 !important',
  display: 'flex',
  flexDirection: 'column',
}

export type TvContent = CalendarEvent | Announcement | Recognition | Award

const KudosTvRoute = () => {
  const { tvToken } = useParams()
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(0)
  const [shuffledContent, setShuffledContent] = useState<TvContent[]>([])
  const { tvContent, refetchContent, isFetched, isError } = useFetchKudosTv(tvToken as string)

  const maxSteps = tvContent?.length || 0

  useEffect(() => {
    if (activeStep === maxSteps - 1) {
      refetchContent()
      setActiveStep(0)
    }
  }, [activeStep, maxSteps, refetchContent])

  useEffect(() => {
    if (isFetched) {
      setShuffledContent(shuffle(tvContent))
    }
    // Adding 'tvContent' causes an infinite render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched])

  if (!isFetched) {
    return <PageLoading />
  }

  if (isError) {
    navigate(AppPaths.anon.userLogin())
    return null
  }

  return (
    <Container sx={containerStyle}>
      <Stack overflow="hidden" justifyContent="center">
        <Swiper
          modules={[Autoplay]}
          pagination={false}
          autoplay={{
            delay: RECOGNITION_ROTATION_INTERVAL,
            disableOnInteraction: false,
          }}
          onSlideChange={swiper => setActiveStep(swiper.realIndex)}
          loop={true}
          autoHeight={true}
          slidesPerView={1}
          style={{ width: '100%' }}>
          {shuffledContent?.map((content, index) => (
            <SwiperSlide key={`kudosTv-${content.__entity_type__}-${index}`}>
              {Math.abs(activeStep - index) <= 2 ? <KudosTvItem item={content} /> : null}
            </SwiperSlide>
          ))}
        </Swiper>
      </Stack>
      <Stack direction="row" my={2} alignItems="center" justifyContent="space-between">
        <RecognizeQRCode />
        {shuffledContent[activeStep] && <ViewPostQRCode tvContent={shuffledContent[activeStep]} />}
      </Stack>
    </Container>
  )
}

export default KudosTvRoute
